import { useCallback, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Button, Spin, Typography, Flex } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~/app/auth/context/AuthContext';
import { ColumnMappingConfirm } from '~/app/components/asset-import/column-mapping-confirm';
import {
  extractHeaders,
  suggestMappingsByOrganization,
} from '~/app/services/asset/asset-import.service';
import { getErrorMessage } from '~/app/utils/getErrorMessage';

const { Title, Paragraph, Text } = Typography;

export function AssetImportMappingScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';
  const { importRequestId = '' } = useParams<{ importRequestId: string }>();
  const { t } = useTranslation('assets');
  const [success, setSuccess] = useState(false);

  const mappingQuery = useQuery({
    queryKey: ['asset-import-mapping', organizationId, importRequestId],
    queryFn: async () => {
      const extractedHeaders = await extractHeaders(
        organizationId,
        importRequestId,
      );
      const cleanedHeaders = extractedHeaders
        .map((header) => header.trim())
        .filter((header) => header.length > 0);

      if (cleanedHeaders.length === 0) {
        throw new Error('No headers were found in the uploaded file.');
      }

      // Org-scoped mapping suggestions (same contract as Matchy).
      const suggestionResponse = await suggestMappingsByOrganization({
        organizationId,
        headers: cleanedHeaders,
      });

      return {
        headers: cleanedHeaders,
        suggestions: suggestionResponse.suggestions,
        availableFields: suggestionResponse.availableFields,
        requiredFields: suggestionResponse.requiredFields,
      };
    },
    enabled: importRequestId.length > 0 && !!organizationId,
  });

  // No confirm-mapping API exists; confirm locally like Matchy.
  const confirmMappingMutation = useMutation({
    mutationFn: async (mapping: Record<string, string>) => mapping,
    onSuccess: () => {
      setSuccess(true);
    },
  });

  const loading = mappingQuery.isLoading;
  const error = mappingQuery.error ?? confirmMappingMutation.error;
  const mappingData = mappingQuery.data;
  const canRenderMapping =
    !success &&
    (mappingData?.headers.length ?? 0) > 0 &&
    (mappingData?.availableFields.length ?? 0) > 0;
  const mappingRenderKey = mappingData
    ? `${importRequestId}:${mappingData.headers.join('|')}:${mappingData.availableFields.join('|')}:${mappingData.requiredFields.join('|')}`
    : importRequestId;

  const handleConfirm = useCallback(
    async (mapping: Record<string, string>) => {
      await confirmMappingMutation.mutateAsync(mapping);
    },
    [confirmMappingMutation],
  );

  return (
    <Flex
      vertical
      gap="large"
      className="app-page"
      style={{ maxWidth: 900, margin: '0 auto' }}
    >
      <Title level={3}>Column Mapping</Title>
      <Paragraph type="secondary" style={{ marginBottom: 8 }}>
        Review and confirm the suggested mapping for your import.
      </Paragraph>
      <Paragraph type="secondary" style={{ marginBottom: 20 }}>
        Import request ID: <Text code>{importRequestId}</Text>
      </Paragraph>

      {loading && (
        <Flex
          justify="center"
          align="center"
          style={{ marginTop: 24, minHeight: 200 }}
        >
          <Spin />
        </Flex>
      )}

      {error && !loading && (
        <Flex vertical gap="small" style={{ marginTop: 16 }}>
          <Alert
            type="error"
            showIcon
            title="Unable to load mapping"
            description={getErrorMessage(
              error,
              t,
              t(
                'importFlow.mappingLoadFailed',
                'Something went wrong while loading column mappings.',
              ),
            )}
            style={{ marginBottom: 12 }}
          />
          <Button
            icon={<ReloadOutlined />}
            loading={mappingQuery.isFetching}
            onClick={() => void mappingQuery.refetch()}
          >
            Retry
          </Button>
        </Flex>
      )}

      {success && (
        <Flex vertical gap="small" style={{ marginTop: 16 }}>
          <Alert
            type="success"
            showIcon
            title="Mapping confirmed"
            description={t(
              'importFlow.mappingConfirmedLocally',
              'Column mapping confirmed. Return to Asset Import to continue.',
            )}
            style={{ marginBottom: 12 }}
          />
          <Button type="primary" onClick={() => navigate('/assets/import')}>
            Back to Asset Import
          </Button>
        </Flex>
      )}

      {mappingData && !loading && !error && !success && canRenderMapping && (
        <ColumnMappingConfirm
          key={mappingRenderKey}
          open
          loading={confirmMappingMutation.isPending}
          headers={mappingData.headers}
          suggestedMappings={mappingData.suggestions}
          availableFields={mappingData.availableFields}
          requiredFields={mappingData.requiredFields}
          onConfirm={handleConfirm}
          onCancel={() => navigate('/assets/import')}
        />
      )}
    </Flex>
  );
}
